import { useState } from "react";
import { router } from "@inertiajs/react";
import { BackofficeLayout } from "@/components/layout/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

type Account = { id: string; name: string; slug: string };

type Props = {
  accounts: Account[];
};

export default function BackofficeBusinessesNew({ accounts }: Props) {
  const [accountId, setAccountId] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedAccount = accounts.find((a) => a.id === accountId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    router.post(
      `/backoffice/businesses`,
      { business: { account_id: accountId, name } },
      { onFinish: () => setSubmitting(false) },
    );
  }

  return (
    <BackofficeLayout
      title="New Business"
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Businesses", href: "/backoffice/businesses" },
        { label: "New" },
      ]}
    >
      <div className="max-w-md">
        <div className="mb-6">
          <h1 className="text-xl font-semibold tracking-tight">New Business</h1>
          <p className="text-sm text-muted-foreground">
            Create a business and assign it to a workspace
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Workspace</Label>
            <Popover open={accountOpen} onOpenChange={setAccountOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={accountOpen}
                  className="w-full justify-between font-normal"
                >
                  {selectedAccount
                    ? selectedAccount.name
                    : "Select workspace..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search workspaces..." />
                  <CommandList>
                    <CommandEmpty>No workspace found.</CommandEmpty>
                    <CommandGroup>
                      {accounts.map((a) => (
                        <CommandItem
                          key={a.id}
                          value={a.name}
                          onSelect={() => {
                            setAccountId(a.id);
                            setAccountOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              accountId === a.id ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {a.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Business Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Corp"
              required
              autoFocus
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={submitting || !accountId}>
              {submitting ? "Creating..." : "Create Business"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => router.visit("/backoffice/businesses")}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </BackofficeLayout>
  );
}
